import { useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";

export function UserProfile() {
  const { user, loading, loggedIn, logout } = useAuth();
  const [notes, setNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);

  const profile = useMemo(() => {
    const raw = (user || {}) as Record<string, unknown>;
    const get = (k: string) =>
      raw[k] === null || raw[k] === undefined || raw[k] === "" ? "--" : String(raw[k]);

    return {
      nickname: get("nickname"),
      avatar: raw.picture ? String(raw.picture) : "",
      country: get("country"),
      steamId: get("steamId"),
      profileUrl: raw.profileUrl ? String(raw.profileUrl) : "",
      realname: get("realname"),
      faceitId:
        raw.faceit && typeof raw.faceit === "object" && (raw.faceit as any).playerId
          ? String((raw.faceit as any).playerId)
          : "--",
    };
  }, [user]);

  return (
    <div className="space-y-10 p-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-4">
          {profile.avatar ? (
            <img
              src={profile.avatar}
              alt="Avatar"
              className="w-20 h-20 rounded-full border border-border object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-full border border-border bg-bg" />
          )}
          <div>
            <h1 className="text-3xl font-bold">{profile.nickname}</h1>
            <p className="text-muted text-sm">
              {loading ? "Loading profile..." : loggedIn ? "Steam profile" : "Not logged in"}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <span className="px-3 py-1 rounded-md bg-border text-muted">
            {loggedIn ? "SIGNED IN" : "GUEST"}
          </span>
          <p className="text-xs text-muted">Join date and activity not available.</p>
        </div>
      </div>

      {/* GRID */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          {/* Account Info */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Account</h2>

            <div className="space-y-3 text-sm">
              <p>
                <span className="text-muted">Steam ID:</span> {profile.steamId || "--"}
              </p>
              <p>
                <span className="text-muted">Real name:</span> {profile.realname}
              </p>
              <p>
                <span className="text-muted">Country:</span> {profile.country}
              </p>
              <p>
                <span className="text-muted">FACEIT ID:</span> {profile.faceitId}
              </p>
              {profile.profileUrl && (
                <p>
                  <span className="text-muted">Profile:</span> {profile.profileUrl}
                </p>
              )}
              {!loggedIn && (
                <p className="text-muted text-sm">Sign in with Steam to see your profile details.</p>
              )}
            </div>
          </section>

          {/* Favorite Teams */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Favorite Teams</h2>

            <div className="space-y-2 text-sm">
              <p className="text-muted text-sm">Coming soon.</p>
            </div>
          </section>
        </div>

        {/* MIDDLE COLUMN */}
        <div className="space-y-6">
          {/* Pinned Opponents */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Pinned Opponents</h2>

            <div className="space-y-2 text-sm">
              <p className="text-muted text-sm">Coming soon.</p>
            </div>
          </section>

          {/* Personal Notes */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Personal Notes</h2>
              <button
                onClick={() => setEditingNotes(!editingNotes)}
                className="text-sm text-muted hover:text-[#4FC3F7] transition"
              >
                {editingNotes ? "Save" : "Edit"}
              </button>
            </div>

            {editingNotes ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="
                  w-full min-h-32 bg-bg border border-border rounded-md p-3
                  focus:outline-none focus:border-[#4FC3F7]
                "
              />
            ) : (
              <p className="text-sm text-muted whitespace-pre-line">
                {notes || "No notes yet."}
              </p>
            )}
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* Activity */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Activity</h2>

            <p className="text-sm text-muted">
              Later, this can show:
              <br />- Recent scouting activity
              <br />- Teams viewed
              <br />- Notes added
              <br />- Match prep sessions
              <br />- PRO features used
            </p>
          </section>

          {/* Settings */}
          <section className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Settings</h2>

            <div className="space-y-3 text-sm">
              <button className="w-full px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition">
                Change Avatar
              </button>
              <button className="w-full px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition">
                Manage Subscription
              </button>
              <button
                onClick={() => void logout()}
                className="w-full px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition"
              >
                Logout
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
